import React, { useState, useEffect } from "react";
import { 
    Button, 
    Space, 
    message, 
    Tag, 
    Col, 
    Select, 
    Avatar, 
    Typography, 
    Tooltip, 
    Modal,
    Divider,
    Row,
    Statistic
} from "antd";
import { 
    PlusOutlined, 
    EditOutlined, 
    ReloadOutlined, 
    CarOutlined, 
    UserOutlined, 
    MailOutlined, 
    PhoneOutlined, 
    CheckCircleOutlined,
    ClockCircleOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    IdcardOutlined,
    CheckOutlined,
    SearchOutlined,
    CloseCircleOutlined,
    SafetyCertificateOutlined,
    BankOutlined
} from "@ant-design/icons";
import DeliveryStaffServ from "../../service/DeliveryStaffServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import AddDeliveryStaff from "../../component/AddDeliveryStaff";
import EmptyState from "../../util/EmptyState";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import TableActions from "../../component/ui/TableActions";
import DataTable from "../../component/ui/DataTable";
import { getDatePresets } from "../../component/ui/QuickFilter";
import { getImageUrl } from "../../component/SafeImage";
import FormSelect from "../../component/ui/FormSelect";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { useAdminTable } from "../hooks/useAdminTable";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const { Option } = Select;

const PRESET_REASONS = [
    { label: '📷 Driving License is blurry', value: 'Driving License image is blurry or unclear. Please upload a high-quality, well-lit photo.' },
    { label: '🪪 Aadhaar not readable', value: 'Aadhaar card number or details are not clearly visible. Please re-upload a clear scan.' },
    { label: '🚗 Vehicle RC missing', value: 'Vehicle Registration Certificate (RC) document is missing. Please upload it.' },
    { label: '🤳 Photo mismatch', value: 'The photo in the document does not match the profile photo. Please re-upload matching documents.' },
    { label: '📄 Wrong document uploaded', value: 'An incorrect or irrelevant document was uploaded. Please upload the correct KYC documents.' },
    { label: '✏️ Custom reason', value: '' },
];

const DeliveryStaff = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [details, setDetails] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [showDocHistory, setShowDocHistory] = useState(false);
    const [kycLoading, setKycLoading] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectionReasonText, setRejectionReasonText] = useState('');
    const [pendingRejectId, setPendingRejectId] = useState(null);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [pendingApproveId, setPendingApproveId] = useState(null);

    const [quickFilter, setQuickFilter] = useState(undefined);

    const fetchStaff = async (params) => {
        const query = {
            limit: params.limit,
            page: params.page
        };
        if (params.search) query.search = params.search;
        if (params.statusFilter && params.statusFilter !== 'all') query.status = params.statusFilter;
        if (params.accountFilter && params.accountFilter !== 'all') {
            // Mapping account filter to query: active -> accountStatus=ACTIVE
            // In the backend, accountStatus is used. 
            // In the frontend, it was checking item.isActive.
            // Let's rely on accountStatus mapping if possible, but let's just pass accountFilter for now.
            query.accountStatus = params.accountFilter === 'active' ? 'APPROVED' : 'INACTIVE';
        }

        if (params.dateRange?.[0]) query.startDate = params.dateRange[0].startOf('day').toISOString();
        if (params.dateRange?.[1]) query.endDate = params.dateRange[1].endOf('day').toISOString();

        const res = await DeliveryStaffServ.index(query);
        return { data: res?.data || [], total: res?.total || 0 };
    };

    const {
        data: staff,
        setData: setStaff,
        total: totalStaff,
        loading,
        params,
        setParams,
        handleSearch,
        refresh
    } = useAdminTable(fetchStaff, "admin_delivery_staff", {
        page: 1, limit: 10, search: "", statusFilter: "all", accountFilter: "all", dateRange: null
    });

    const handleAddStaffOpen = () => {
        setDetails(null);
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const handleEditStaff = (record) => {
        setDetails(record);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleKycAction = async (staffId, newStatus, rejectionReason = null) => {
        const label = (newStatus === 'ACTIVE' || newStatus === 'APPROVED') ? 'approve' : 'reject';
        try {
            setKycLoading(true);
            const payload = { accountStatus: newStatus };
            if (newStatus === 'REJECTED' && rejectionReason) {
                payload.rejectionReason = rejectionReason;
                payload.rejectionDate = new Date().toISOString();
            } else if (newStatus === 'ACTIVE' || newStatus === 'APPROVED') {
                // Clearing rejection fields and auto-activating when approving
                payload.rejectionReason = null;
                payload.rejectionDate = null;
                payload.isActive = true;
            }
            await DeliveryStaffServ.update(staffId, payload);
            message.success(`Staff member ${label}d successfully`);
            // Refresh list and update selected staff in modal
            refresh();
            setSelectedStaff(prev => prev ? { ...prev, accountStatus: newStatus, rejectionReason: payload.rejectionReason } : prev);
        } catch (err) {
            message.error(`Failed to ${label} staff member`);
        } finally {
            setKycLoading(false);
        }
    };

    const handleRejectClick = (staffId) => {
        setPendingRejectId(staffId);
        setRejectionReasonText('');
        setIsRejectModalOpen(true);
    };

    const handleRejectSubmit = async () => {
        if (!pendingRejectId || rejectionReasonText.trim().length < 10) return;
        await handleKycAction(pendingRejectId, 'REJECTED', rejectionReasonText.trim());
        setIsRejectModalOpen(false);
        setPendingRejectId(null);
        setRejectionReasonText('');
    };

    const handleApproveClick = (staffId) => {
        setPendingApproveId(staffId);
        setIsApproveModalOpen(true);
    };

    const handleApproveSubmit = async () => {
        if (!pendingApproveId) return;
        await handleKycAction(pendingApproveId, 'APPROVED');
        setIsApproveModalOpen(false);
        setPendingApproveId(null);
    };

    const fetchAllStaff = async () => {
        const res = await DeliveryStaffServ.index({ limit: 10000 });
        return res?.data || [];
    };

    const columns = [
        {
            title: "Name",
            key: "name",
            render: (_, record) => (
                <span
                    style={{
                        color: "#1890ff",
                        fontWeight: 600,
                        fontSize: '14px'
                    }}
                >
                    {`${record.firstName || ''} ${record.lastName || ''}`}
                </span>
            ),
        },
        { 
            title: "Email", 
            dataIndex: "email", 
            key: "email",
            render: (email) => <Text style={{ color: '#475569' }}>{email || "—"}</Text>
        },
        { 
            title: "Phone", 
            dataIndex: "phone", 
            key: "phone",
            render: (phone) => <Text style={{ color: '#475569' }}>{phone || "—"}</Text>
        },
        {
            title: "Status",
            dataIndex: "isAvailable",
            key: "status",
            align: 'center',
            render: (avail) => (
                <Tag 
                    color={avail ? "success" : "orange"} 
                    style={{ borderRadius: '6px', fontWeight: 700, fontSize: '11px' }}
                >
                    {avail ? "AVAILABLE" : "BUSY"}
                </Tag>
            )
        },
        { 
            title: "Vehicle No.", 
            dataIndex: "vehicleNumber", 
            key: "vehicle",
            render: (no) => <Text strong style={{ color: '#1e293b' }}>{no || "—"}</Text>
        },
        {
            title: "KYC Status",
            dataIndex: "accountStatus",
            key: "accountStatus",
            align: 'center',
            render: (status) => {
                const cfg = {
                    ACTIVE:      { color: 'success',   label: 'ACTIVE' },
                    APPROVED:    { color: 'success',   label: 'APPROVED' },
                    KYC_PENDING: { color: 'warning',   label: 'KYC PENDING' },
                    INCOMPLETE:  { color: 'default',   label: 'INCOMPLETE' },
                    REJECTED:    { color: 'error',     label: 'REJECTED' },
                    SUSPENDED:   { color: 'volcano',   label: 'SUSPENDED' },
                };
                const { color, label } = cfg[status] || { color: 'default', label: status || '—' };
                return (
                    <Tag color={color} style={{ borderRadius: '6px', fontWeight: 700, fontSize: '11px' }}>
                        {label}
                    </Tag>
                );
            }
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStaff(record);
                        setIsDetailsModalOpen(true);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <>
        <div className="sd-container" style={{ padding: '24px' }}>
            <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <CarOutlined style={{ fontSize: '22px', color: '#16a34a' }} />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Delivery Staff</h1>
                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{totalStaff} registered staff members</p>
                    </div>
                </div>

                <Space size={12}>
                    <TableActions
                        onFetchFullData={fetchAllStaff}
                        columns={columns}
                        fileName="delivery_staff_list"
                        pageName="Delivery Staff"
                    />
                    <PrimaryButton
                        icon={<PlusOutlined />}
                        onClick={handleAddStaffOpen}
                        text="Add Staff Member"
                        style={{
                            height: '40px',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)',
                            border: 'none',
                            padding: '0 20px',
                            fontSize: '13px',
                            fontWeight: 700,
                            boxShadow: '0 4px 12px rgba(255, 136, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    />
                </Space>
            </div>

            <div className="pcu-padding-0">
                {/* Row 1: Filters matching Image 2 */}
                <div className="ss-filter-bar" style={{ background: 'transparent', border: 'none', padding: '0 0 16px 0', display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 1.6fr', gap: '12px' }}>
                    <div className="ss-filter-field">
                        <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>SEARCH STAFF</span>
                        <div className="ss-search-wrap">
                            <SearchOutlined className="ss-search-icon" style={{ color: '#94a3b8' }} />
                            <input
                                className="ss-search-input"
                                placeholder="Search by name, email..."
                                value={params.search}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{ height: '38px', borderRadius: '10px', fontSize: '13px' }}
                            />
                        </div>
                    </div>

                    <div className="ss-filter-field">
                        <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>STATUS</span>
                        <Select
                            value={params.statusFilter}
                            onChange={(val) => setParams(prev => ({ ...prev, statusFilter: val, page: 1 }))}
                            style={{ width: '100%', height: '38px' }}
                            className="pcu-radius-10"
                        >
                            <Option value="all">All Status</Option>
                            <Option value="available">Available</Option>
                            <Option value="busy">Busy</Option>
                        </Select>
                    </div>

                    <div className="ss-filter-field">
                        <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>ACCOUNT STATUS</span>
                        <Select
                            value={params.accountFilter}
                            onChange={(val) => setParams(prev => ({ ...prev, accountFilter: val, page: 1 }))}
                            style={{ width: '100%', height: '38px' }}
                            className="pcu-radius-10"
                        >
                            <Option value="all">All Account Status</Option>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                        </Select>
                    </div>

                    <div className="ss-filter-field">
                        <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>QUICK FILTER</span>
                        <Select
                            value={quickFilter}
                            onChange={(key) => {
                                setQuickFilter(key);
                                const presets = getDatePresets();
                                const preset = presets.find(p => p.value === key);
                                setParams(prev => ({ ...prev, dateRange: preset ? preset.range : null, page: 1 }));
                            }}
                            placeholder="Select Preset"
                            style={{ width: '100%', height: '38px' }}
                            className="pcu-radius-10"
                            allowClear
                        >
                            {getDatePresets().map(p => (
                                <Option key={p.value} value={p.value}>{p.label}</Option>
                            ))}
                        </Select>
                    </div>

                    <div className="ss-filter-field" style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>JOINED RANGE</span>
                            <RangePicker
                                value={params.dateRange}
                                onChange={(val) => {
                                    setParams(prev => ({ ...prev, dateRange: val, page: 1 }));
                                    setQuickFilter(undefined);
                                }}
                                style={{ width: '100%', height: '38px', borderRadius: '10px' }}
                            />
                        </div>
                        <button
                            className="ss-reset-btn"
                            onClick={() => {
                                setParams({ page: 1, limit: 10, search: "", statusFilter: "all", accountFilter: "all", dateRange: null });
                                setQuickFilter(undefined);
                                refresh();
                            }}
                            style={{
                                height: '38px',
                                width: '38px',
                                padding: '0',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: 600,
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                color: '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title="Reset Filters"
                        >
                            <ReloadOutlined />
                        </button>
                    </div>
                </div>

                <div className="ss-table-wrap">
                    <DataTable
                        className="premium-data-table"
                        columns={columns}
                        data={staff}
                        rowKey="_id"
                        onRow={(record) => ({
                            style: { cursor: 'default' }
                        })}
                        locale={{
                            emptyText: (
                                <div className="pcu-padding-y-60">
                                    <EmptyState
                                        title="No Delivery Staff"
                                        description={params.search
                                            ? "No staff members match your search."
                                            : "You haven't added any delivery staff yet."
                                        }
                                        gradient="green"
                                        onAction={!params.search ? handleAddStaffOpen : undefined}
                                        actionText="Add Staff Member"
                                        icon="inbox"
                                    />
                                </div>
                            )
                        }}
                        loading={loading}
                        pagination={{
                            current: params.page,
                            pageSize: params.limit,
                            total: totalStaff,
                            showTotal: (total) => <span className="pagination-total">Total {total} items</span>,
                            onChange: (p) => {
                                setParams(prev => ({ ...prev, page: p }));
                                const pageContent = document.querySelector('.page-content');
                                if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                else window.scrollTo({ top: 0, behavior: 'smooth' });
                            },
                            onShowSizeChange: (_, size) => setParams(prev => ({ ...prev, limit: size, page: 1 })),
                            position: ["bottomRight"],
                            size: "small"
                        }}
                    />
                </div>
            </div>
            {isModalOpen && (
                <AddDeliveryStaff
                    open={isModalOpen}
                    details={details}
                    isEditing={isEditing}
                    onClose={() => setIsModalOpen(false)}
                    refresh={refresh}
                />
            )}

            {/* Delivery Staff Details Modal */}
            <Modal
                title={null}
                open={isDetailsModalOpen}
                onCancel={() => setIsDetailsModalOpen(false)}
                footer={null}
                width={700}
                centered
                closable={false}
                bodyStyle={{ padding: 0 }}
                maskStyle={{ backdropFilter: 'blur(3px)' }}
                style={{ borderRadius: 20, overflow: 'hidden' }}
                className="premium-modal"
            >
                {selectedStaff && (
                    <div className="pcu-flex-column">
                        {/* Header Section - Premium Style */}
                        <div style={{ 
                            padding: '16px 20px', 
                            background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)', 
                            borderBottom: '1px solid #e2e8f0', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 14, 
                            position: 'relative' 
                        }}>
                            <button 
                                onClick={() => { setIsDetailsModalOpen(false); setShowDocHistory(false); }}
                                style={{ position: 'absolute', right: 16, top: 16, background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                            >
                                <span style={{ fontFamily: 'sans-serif', fontSize: '18px', lineHeight: '1' }}>×</span>
                            </button>
                            
                            <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0, background: '#f8fafc', boxShadow: '0 4px 10px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Avatar
                                    size={50}
                                    src={selectedStaff.profilePhoto ? getImageUrl(selectedStaff.profilePhoto) : null}
                                    icon={<UserOutlined style={{ color: '#4f46e5' }} />}
                                    style={{ borderRadius: 8, background: 'transparent' }}
                                />
                            </div>
                            
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>{`${selectedStaff.firstName || ''} ${selectedStaff.lastName || ''}`.trim()}</h2>
                                    <Tag color={selectedStaff.isActive ? "success" : "default"} style={{ margin: 0, borderRadius: 5, padding: '0 8px', fontSize: 9, fontWeight: 700, border: 'none', background: '#fff', color: selectedStaff.isActive ? '#16a34a' : '#94a3b8', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        {selectedStaff.isActive ? "ACTIVE" : "INACTIVE"}
                                    </Tag>
                                </div>
                                <p style={{ margin: '1px 0 0', fontSize: 11, color: '#64748b', fontWeight: 600 }}>ID: <span style={{ color: '#4f46e5' }}>{selectedStaff._id?.substring(0, 12).toUpperCase()}</span> • DELIVERY STAFF</p>
                            </div>
                        </div>

                        {/* Detail Grid Section - Compact */}
                        <div style={{ padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: '#fff' }}>
                            {/* Email - Full Width */}
                            <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}><MailOutlined /> Email Address</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 600, lineHeight: 1.3 }}>{selectedStaff.email}</div>
                            </div>

                            {/* Phone & Vehicle */}
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><PhoneOutlined /> Phone Number</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedStaff.phone || 'N/A'}</div>
                            </div>
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><CarOutlined /> Vehicle No.</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedStaff.vehicleNumber || 'N/A'}</div>
                            </div>

                            {/* Dates */}
                            <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><CalendarOutlined /> Joined Date</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>
                                    {new Date(selectedStaff.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>

                            {/* Status Info */}
                            <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '6px 12px', border: '1px solid #bcf0da' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#16a34a', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircleOutlined /> Availability</div>
                                <div style={{ fontSize: 14, color: '#166534', fontWeight: 800 }}>{selectedStaff.isAvailable ? "AVAILABLE NOW" : "CURRENTLY BUSY"}</div>
                            </div>
                            <div style={{ background: '#eff6ff', borderRadius: 8, padding: '6px 12px', border: '1px solid #bfdbfe' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#3b82f6', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><CarOutlined /> Vehicle Type</div>
                                <div style={{ fontSize: 14, color: '#1e40af', fontWeight: 800 }}>{selectedStaff.vehicleType ? selectedStaff.vehicleType.charAt(0).toUpperCase() + selectedStaff.vehicleType.slice(1) : "—"}</div>
                            </div>
                        </div>

                        {/* ── Bank Details Section ─────────────────────────── */}
                        <div style={{ padding: '0 14px 14px' }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                                <BankOutlined /> Bank Details
                            </div>
                            {selectedStaff.bankDetails && selectedStaff.bankDetails.accountNumber ? (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Bank Name</div>
                                        <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedStaff.bankDetails.bankName || '—'}</div>
                                    </div>
                                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Account Holder</div>
                                        <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedStaff.bankDetails.accountHolderName || selectedStaff.bankDetails.accountHolder || '—'}</div>
                                    </div>
                                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Account Number</div>
                                        <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedStaff.bankDetails.accountNumber || '—'}</div>
                                    </div>
                                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>IFSC Code</div>
                                        <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedStaff.bankDetails.ifscCode || '—'}</div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    background: '#fafafa', border: '1.5px dashed #e2e8f0',
                                    borderRadius: 8, padding: '10px 14px',
                                    display: 'flex', alignItems: 'center', gap: 10, color: '#94a3b8',
                                }}>
                                    <BankOutlined style={{ fontSize: 18, opacity: 0.4 }} />
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>No bank details configured</div>
                                        <div style={{ fontSize: 9, color: '#cbd5e1' }}>Staff member has not submitted bank details yet.</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── KYC Documents Section ─────────────────────────── */}
                        <div style={{ padding: '0 14px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <IdcardOutlined /> KYC Documents
                                </div>
                                {(() => {
                                    if (!selectedStaff.kycDocuments || selectedStaff.kycDocuments.length === 0) return null;
                                    const latestDoc = selectedStaff.kycDocuments[selectedStaff.kycDocuments.length - 1];
                                    const latestTime = new Date(latestDoc.uploadedAt).getTime();
                                    const latestBatch = selectedStaff.kycDocuments.filter(doc => 
                                        Math.abs(latestTime - new Date(doc.uploadedAt).getTime()) < 5 * 60 * 1000
                                    );
                                    const historyCount = selectedStaff.kycDocuments.length - latestBatch.length;
                                    
                                    if (historyCount > 0) {
                                        return (
                                            <button 
                                                onClick={() => setShowDocHistory(!showDocHistory)}
                                                style={{ 
                                                    background: 'none', border: 'none', padding: 0, 
                                                    color: '#3b82f6', fontSize: 10, fontWeight: 700, 
                                                    cursor: 'pointer', textDecoration: 'underline' 
                                                }}
                                            >
                                                {showDocHistory ? 'Hide History' : `View History (${historyCount})`}
                                            </button>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>

                            {selectedStaff.kycDocuments && selectedStaff.kycDocuments.length > 0 ? (
                                 <div>
                                     {(() => {
                                         const latestDoc = selectedStaff.kycDocuments[selectedStaff.kycDocuments.length - 1];
                                         const latestTime = new Date(latestDoc.uploadedAt).getTime();
                                         const latestBatch = selectedStaff.kycDocuments.filter(doc => 
                                             Math.abs(latestTime - new Date(doc.uploadedAt).getTime()) < 5 * 60 * 1000
                                         );
                                         const previousDocs = selectedStaff.kycDocuments.filter(doc => 
                                             !latestBatch.some(d => d._id === doc._id || d.documentUrl === doc.documentUrl)
                                         );

                                         const renderDocCard = (doc, isLatest) => {
                                             const idx = selectedStaff.kycDocuments.findIndex(d => d._id === doc._id || d.documentUrl === doc.documentUrl);
                                             const imgSrc = getImageUrl(doc.documentUrl);
                                             return (
                                                 <a
                                                     key={doc._id || idx}
                                                     href={imgSrc}
                                                     target="_blank"
                                                     rel="noopener noreferrer"
                                                     style={{ display: 'block', textDecoration: 'none' }}
                                                 >
                                                     <div style={{
                                                         width: 110, height: 85, borderRadius: 8, overflow: 'hidden',
                                                         border: doc.isVerified ? '2px solid #86efac' : '2px solid #fde68a',
                                                         position: 'relative', background: '#f8fafc',
                                                         boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
                                                         cursor: 'pointer',
                                                         transition: 'transform 0.15s',
                                                         opacity: isLatest ? 1 : 0.65,
                                                     }}
                                                         onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.opacity = '1'; }}
                                                         onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = isLatest ? '1' : '0.65'; }}
                                                     >
                                                         {/* Badge */}
                                                         <div style={{
                                                             position: 'absolute', top: 0, right: 0,
                                                             background: isLatest ? '#3b82f6' : '#94a3b8',
                                                             color: '#fff', fontSize: 7, fontWeight: 800, padding: '2px 6px',
                                                             borderBottomLeftRadius: 6, zIndex: 2
                                                         }}>
                                                             {isLatest ? 'LATEST' : 'PREVIOUS'}
                                                         </div>

                                                         <img
                                                             src={imgSrc}
                                                             alt={`KYC doc ${idx + 1}`}
                                                             style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                             onError={e => {
                                                                 e.target.style.display = 'none';
                                                                 e.target.parentNode.style.background = '#f1f5f9';
                                                             }}
                                                         />
                                                         {/* Verified badge */}
                                                         <div style={{
                                                             position: 'absolute', bottom: 0, left: 0, right: 0,
                                                             background: doc.isVerified ? 'rgba(22,163,74,0.85)' : 'rgba(234,179,8,0.85)',
                                                             padding: '2px 0', textAlign: 'center',
                                                             fontSize: 8, fontWeight: 800, color: '#fff', letterSpacing: '0.3px',
                                                         }}>
                                                             {doc.isVerified ? '✓ VERIFIED' : '⏳ PENDING'}
                                                         </div>
                                                     </div>
                                                     <div style={{ fontSize: 9, color: '#64748b', fontWeight: 600, marginTop: 3, textAlign: 'center', textTransform: 'uppercase' }}>
                                                         {doc.documentType?.replace('_', ' ') || 'DOCUMENT'}
                                                     </div>
                                                     <div style={{ fontSize: 8, color: '#94a3b8', fontWeight: 500, textAlign: 'center', marginTop: 1 }}>
                                                         {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                                     </div>
                                                 </a>
                                             );
                                         };

                                         return (
                                             <div>
                                                 {/* Latest Uploads Title */}
                                                 <div style={{ fontSize: 10, fontWeight: 700, color: '#1e293b', marginBottom: 6, marginTop: 4 }}>
                                                     Latest Submission ({latestBatch[0]?.uploadedAt ? new Date(latestBatch[0].uploadedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''})
                                                 </div>
                                                 <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: showDocHistory && previousDocs.length > 0 ? 16 : 0 }}>
                                                     {latestBatch.map(doc => renderDocCard(doc, true))}
                                                 </div>

                                                 {/* Previous Uploads Title & Grid */}
                                                 {showDocHistory && previousDocs.length > 0 && (
                                                     <div>
                                                         <div style={{ borderTop: '1px dashed #e2e8f0', margin: '12px 0 8px' }} />
                                                         <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                                                             Previous Rejection History / Old Submissions
                                                         </div>
                                                         <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                             {previousDocs.map(doc => renderDocCard(doc, false))}
                                                         </div>
                                                     </div>
                                                 )}
                                             </div>
                                         );
                                     })()}
                                 </div>
                            ) : (
                                <div style={{
                                    background: '#fafafa', border: '1.5px dashed #e2e8f0',
                                    borderRadius: 8, padding: '14px 16px',
                                    display: 'flex', alignItems: 'center', gap: 10, color: '#94a3b8',
                                }}>
                                    <IdcardOutlined style={{ fontSize: 20, opacity: 0.4 }} />
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8' }}>No documents uploaded</div>
                                        <div style={{ fontSize: 10, color: '#cbd5e1' }}>Staff member has not submitted any KYC documents yet.</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer Section - Compact */}
                        <div style={{ padding: '12px 16px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 8 }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button 
                                    style={{
                                        background: '#eff6ff', border: 'none', color: '#3b82f6', fontSize: 12, fontWeight: 700,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6
                                    }}
                                    onClick={() => {
                                        setIsDetailsModalOpen(false);
                                        handleEditStaff(selectedStaff);
                                    }}
                                >
                                    <EditOutlined /> Edit Profile
                                </button>

                                {/* KYC Approve button — shown when pending */}
                                {(selectedStaff.accountStatus === 'KYC_PENDING' || selectedStaff.accountStatus === 'INCOMPLETE') && (
                                    <button
                                        disabled={kycLoading}
                                        style={{
                                            background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', fontSize: 12, fontWeight: 700,
                                            cursor: kycLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '6px 14px', borderRadius: 6, opacity: kycLoading ? 0.7 : 1
                                        }}
                                        onClick={() => handleApproveClick(selectedStaff._id)}
                                    >
                                        <SafetyCertificateOutlined /> Approve KYC
                                    </button>
                                )}

                                {/* KYC Reject button — shown when pending or active */}
                                {(selectedStaff.accountStatus === 'KYC_PENDING' || selectedStaff.accountStatus === 'INCOMPLETE' || selectedStaff.accountStatus === 'ACTIVE' || selectedStaff.accountStatus === 'APPROVED') && (
                                    <button
                                        disabled={kycLoading}
                                        style={{
                                            background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: 12, fontWeight: 700,
                                            cursor: kycLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '6px 14px', borderRadius: 6, opacity: kycLoading ? 0.7 : 1
                                        }}
                                        onClick={() => handleRejectClick(selectedStaff._id)}
                                    >
                                        <CloseCircleOutlined /> {selectedStaff.accountStatus === 'ACTIVE' || selectedStaff.accountStatus === 'APPROVED' ? 'Revoke Approval (Reject)' : 'Reject'}
                                    </button>
                                )}

                                {/* Show stored rejection reason if already rejected */}
                                {selectedStaff.accountStatus === 'REJECTED' && selectedStaff.rejectionReason && (
                                    <div style={{ width: '100%', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '6px 12px', marginTop: 4 }}>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', marginBottom: 2 }}>Rejection Reason</div>
                                        <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>{selectedStaff.rejectionReason}</div>
                                    </div>
                                )}

                                {/* Re-activate button — shown when rejected/suspended */}
                                {(selectedStaff.accountStatus === 'REJECTED' || selectedStaff.accountStatus === 'SUSPENDED') && (
                                    <button
                                        disabled={kycLoading}
                                        style={{
                                            background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c', fontSize: 12, fontWeight: 700,
                                            cursor: kycLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '6px 14px', borderRadius: 6, opacity: kycLoading ? 0.7 : 1
                                        }}
                                        onClick={() => handleApproveClick(selectedStaff._id)}
                                    >
                                        <CheckOutlined /> Re-Activate
                                    </button>
                                )}
                            </div>

                            <button 
                                onClick={() => { setIsDetailsModalOpen(false); setShowDocHistory(false); }}
                                style={{
                                    padding: '6px 20px', borderRadius: 8, border: '1px solid #e2e8f0',
                                    background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 700,
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>

            {/* Rejection Reason Modal */}
            <Modal
                title={<span style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>Reject KYC Application</span>}
                open={isRejectModalOpen}
                onCancel={() => { setIsRejectModalOpen(false); setPendingRejectId(null); setRejectionReasonText(''); }}
                onOk={handleRejectSubmit}
                okText="Submit Rejection"
                cancelText="Cancel"
                okButtonProps={{
                    disabled: rejectionReasonText.trim().length < 10,
                    danger: true,
                    style: { fontWeight: 700 }
                }}
                centered
                width={500}
            >
                <div style={{ padding: '4px 0 8px' }}>
                    <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
                        Select a preset reason or type a custom one. The delivery staff will see this and can resubmit corrected documents.
                    </p>

                    {/* Preset Reason Chips */}
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Quick Select</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                        {PRESET_REASONS.map((preset) => {
                            const isSelected = preset.value !== ''
                                ? rejectionReasonText === preset.value
                                : (rejectionReasonText !== '' && !PRESET_REASONS.some(p => p.value && p.value === rejectionReasonText));
                            return (
                                <button
                                    key={preset.label}
                                    onClick={() => setRejectionReasonText(preset.value)}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 20,
                                        border: isSelected ? '1.5px solid #dc2626' : '1.5px solid #e2e8f0',
                                        background: isSelected ? '#fef2f2' : '#f8fafc',
                                        color: isSelected ? '#dc2626' : '#475569',
                                        fontSize: 12,
                                        fontWeight: isSelected ? 700 : 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {preset.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Reason Text Area */}
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Rejection Reason *</div>
                    <textarea
                        value={rejectionReasonText}
                        onChange={e => setRejectionReasonText(e.target.value)}
                        placeholder="Type a custom rejection reason here..."
                        rows={4}
                        style={{
                            width: '100%',
                            border: '1.5px solid',
                            borderColor: rejectionReasonText.trim().length >= 10 ? '#fca5a5' : '#e2e8f0',
                            borderRadius: 8,
                            padding: '10px 12px',
                            fontSize: 13,
                            color: '#334155',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            outline: 'none',
                            boxSizing: 'border-box',
                            background: '#fff',
                        }}
                    />
                    <div style={{ fontSize: 11, color: rejectionReasonText.trim().length < 10 ? '#ef4444' : '#22c55e', marginTop: 4, fontWeight: 600 }}>
                        {rejectionReasonText.trim().length < 10
                            ? `Minimum 10 characters required (${rejectionReasonText.trim().length}/10)`
                            : `✓ Ready to submit (${rejectionReasonText.trim().length} chars)`
                        }
                    </div>
                </div>
            </Modal>

            {/* Approval Confirmation Modal */}
            <Modal
                title={<span style={{ fontSize: 15, fontWeight: 800, color: '#16a34a' }}>Approve Delivery Staff</span>}
                open={isApproveModalOpen}
                onCancel={() => { setIsApproveModalOpen(false); setPendingApproveId(null); }}
                onOk={handleApproveSubmit}
                okText="Yes, Approve Staff"
                cancelText="Cancel"
                okButtonProps={{
                    style: { background: '#16a34a', borderColor: '#16a34a', fontWeight: 700 }
                }}
                centered
                width={400}
            >
                <div style={{ padding: '16px 0', textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>
                        Welcome to Nearzon!
                    </div>
                    <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                        Are you sure you want to approve this KYC application? This will activate their account and they can start accepting delivery orders immediately.
                    </p>
                </div>
            </Modal>
        </>
    );
};

export default DeliveryStaff;
